import React from "react";
import { Typography, TypographyProps, Link } from "@mui/material";

type LinkifyTextProps = {
  text: string;
  typographyProps?: TypographyProps;
};

/**
 * Renders text with clickable URLs using Material UI Link component.
 * This component automatically detects URLs in the text and converts them to clickable links.
 * 
 * @param text - The text content that may contain URLs
 * @param typographyProps - Optional Material-UI Typography props to customize text styling
 * @returns A React component with linkified text
 */
export function LinkifyText({ text, typographyProps = {} }: LinkifyTextProps) {
  const defaultTypographyProps: TypographyProps = {
    variant: "body2",
    sx: { whiteSpace: "break-spaces" },
    ...typographyProps,
  };

  // Regex to match URLs (http, https, www)
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = text.split(urlRegex);

  const parsedContent = parts.map((part, index) => {
    if (part.match(urlRegex)) {
      // Add https:// to www. URLs
      const href = part.startsWith('www.') ? `https://${part}` : part;
      return (
        <Link
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          sx={{ wordBreak: 'break-word', cursor: 'pointer' }}
        >
          {part}
        </Link>
      );
    }
    // Return the text directly for non-URL parts, or null for empty strings
    return part ? <span key={index}>{part}</span> : null;
  }).filter(Boolean); // Remove null values

  return (
    <Typography {...defaultTypographyProps}>
      {parsedContent}
    </Typography>
  );
}

export default LinkifyText;